import React, { ReactNode, useMemo, useState } from 'react';
import _ from 'lodash';

// Material UI
import CheckIcon from '@material-ui/icons/Check';
import TableCell from '@material-ui/core/TableCell';

// Components
import { PlayerScores } from './player-scores.component';
import { EvaluatorSkillScores } from './evaluator-skill-scores.component';
import { IEnhancedTableColumn, IEnhancedTableRow } from '../../_core/_ui/table.components';
import { IFormOption } from '../../_core/_ui/forms.component';
import { numToTime } from '../../../lib/helpers/conversion.helpers';
import { sortSessionsNewestFirst } from '../../../lib/helpers/assessment-session.helpers';
import { successColor } from '../../../lib/theme';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { SportTerminology } from '../../../lib/types';

const EMPTY_CELL = '—';
// The table sorts on `row.data[path]`, so the event column's sort field carries the drill name.
const DRILL_SORT_PATH = 'name';
const SUBMITTED_LABEL = 'Scores submitted';

interface IPlayerScoresContainer {
  assessmentSessions: IAssessmentSession[];
  skills: ISkill[];
  drills: IDrill[];
  members: IMember[];
  scores: IScore[];
  submissions: IScoreSubmission[];
  canViewScoreDetails: boolean;
  setActiveAssessmentSessionId: Function;
}

export const PlayerScoresContainer: React.FC<IPlayerScoresContainer> = ({
  assessmentSessions,
  skills,
  drills,
  members,
  scores,
  submissions,
  canViewScoreDetails,
  setActiveAssessmentSessionId
}) => {
  const termLookup = useCompanySportTerminology();

  const [activeOption, setActiveOption] = useState<IFormOption>();

  const options = useMemo(() => sortSessionsNewestFirst(assessmentSessions).map(assessmentSession => ({
    value: assessmentSession._id,
    label: `${assessmentSession.date} - ${numToTime(assessmentSession.start)}`
  })), [assessmentSessions]);

  // What each evaluator's cell shows for a given event: the per-skill scores for viewers
  // cleared to see them, otherwise a checkmark marking that they submitted at all.
  const cellsByEventAndEvaluator = useMemo(() => {
    const cells: { [drillId: string]: { [userId: string]: ReactNode } } = {};

    if (!canViewScoreDetails) {
      _.each(submissions, ({ drillId, userId }) => {
        _.set(cells, [drillId, userId], <CheckIcon fontSize="small" style={{ color: successColor }} titleAccess={SUBMITTED_LABEL} />);
      });

      return cells;
    }

    const skillsById = _.keyBy(skills, '_id');
    const scoreHash: { [key: string]: { [key: string]: { [key: string]: { [key: number]: IScore } } } } = {};
    _.each(scores, score => {
      _.set(scoreHash, [score.drillId, score.userId, score.skillId, score.round], score);
    });

    _.each(scoreHash, (userScoreHash, drillId) => {
      _.each(userScoreHash, (skillScoreHash, userId) => {
        _.set(cells, [drillId, userId], _.map(skillScoreHash, (roundScores, skillId) => (
          <EvaluatorSkillScores key={skillId} skillName={skillsById[skillId]?.name} roundScores={roundScores} />
        )));
      });
    });

    return cells;
  }, [canViewScoreDetails, scores, submissions, skills]);

  const evaluatorIds = useMemo(
    () => _.uniq(_.flatMap(cellsByEventAndEvaluator, byEvaluator => _.keys(byEvaluator))),
    [cellsByEventAndEvaluator]
  );

  const columns: IEnhancedTableColumn[] = useMemo(() => {
    const usersById = _.keyBy(_.map(members, 'user'), '_id');

    return [
      { key: 'drillId', label: termLookup[SportTerminology.EVENT], path: DRILL_SORT_PATH },
      ...evaluatorIds.map(userId => ({ key: userId, label: usersById[userId]?.name, disableSort: true }))
    ];
  }, [evaluatorIds, members, termLookup]);

  const rows: IEnhancedTableRow[] = useMemo(() => {
    const drillsById = _.keyBy(drills, '_id');

    return _.map(cellsByEventAndEvaluator, (byEvaluator, drillId) => {
      const name = drillsById[drillId]?.name;

      return {
        _id: drillId,
        data: { _id: drillId, [DRILL_SORT_PATH]: name },
        cells: [
          <TableCell key="drill">{name}</TableCell>,
          ...evaluatorIds.map(userId => (
            <TableCell key={userId} align={canViewScoreDetails ? undefined : 'center'}>
              {byEvaluator[userId] || EMPTY_CELL}
            </TableCell>
          ))
        ]
      };
    });
  }, [cellsByEventAndEvaluator, evaluatorIds, drills, canViewScoreDetails]);

  const onChangeAssessmentSession = (option: IFormOption) => {
    setActiveAssessmentSessionId(option.value);
    setActiveOption(option);
  };

  return <PlayerScores
    activeOption={activeOption}
    setOption={onChangeAssessmentSession}
    options={options}
    columns={columns}
    rows={rows}
    defaultOrder={DRILL_SORT_PATH}
    align={canViewScoreDetails ? undefined : 'center'}
  />;
};
